import { Auth0Plugin, createAuth0, useAuth0 } from '@auth0/auth0-vue'
import { ThymeAuth0Error } from '@thyme/libs/src/errors/errors'
import { getLogger } from '@thyme/libs/src/logging/logger'
import {
  isPreview,
  previewAuthRedirectUri,
  setPreviewReturnPath,
} from '@thyme/libs/src/preview/previewAuth'
import { Config } from '@/libs/config/types'

import type { Ref } from 'vue'

export const ENTITY_ID_CLAIM = 'https://thymecare.com/entity-id'

export const RECOVERABLE_AUTH_ERRORS = ['Login required']

/**
 *
 */
function authCallbackUrl(): string {
  return new URL(
    'auth/callback',
    `${window.location.origin}${import.meta.env.BASE_URL}`
  ).href
}

/**
 * Previews share one origin and only `/auth-forward` is a registered Auth0
 * callback for it, so previews hand off to the forwarder instead of going
 * straight to this PR's base-pathed callback route.
 */
export function redirectUri(): string {
  return isPreview() ? previewAuthRedirectUri() : authCallbackUrl()
}

class Auth {
  private static instance: Auth | null = null
  readonly auth0: Auth0Plugin

  private constructor(private config: Config) {
    this.auth0 = createAuth0(
      {
        domain: config.auth0.domain,
        clientId: config.auth0.clientId,
        useRefreshTokens: true,
        useRefreshTokensFallback: true,
        authorizationParams: {
          redirect_uri: redirectUri(),
          audience: this.config.auth0.audience,
        },
        cacheLocation: 'localstorage',
      },
      // Skip this automatically, since we handle it in the AuthCallback component
      { skipRedirectCallback: true }
    )
  }

  static initialize(config: Config): Auth {
    if (!Auth.instance) {
      Auth.instance = new Auth(config)
    }
    return Auth.instance
  }

  static getInstance(): Auth {
    if (!Auth.instance) {
      throw new Error('Auth not initialized')
    }
    return Auth.instance
  }

  async getAccessToken(): Promise<string> {
    try {
      // A silent refresh can fall back to a redirect, which leaves for Auth0
      // and comes back through the forwarder — so the return path must already
      // be recorded here too.
      if (isPreview()) {
        setPreviewReturnPath()
      }
      const token = await this.auth0.getAccessTokenSilently()
      if (token == null) {
        throw new Error('No access token returned')
      }
      return token
    } catch (err) {
      const error = ThymeAuth0Error.fromError(err)
      getLogger().error('failed to get access token', error)
      throw error
    }
  }

  getLoggedInEntityId(): string {
    const claims = this.auth0.idTokenClaims.value

    try {
      if (!claims) {
        throw new Error('No claims found in Auth0 client')
      }
      const entityId = claims[ENTITY_ID_CLAIM] as string | undefined
      if (!entityId) {
        throw new Error(
          'No entity_id found in user metadata for logged in user'
        )
      }
      return entityId
    } catch (err) {
      const error = ThymeAuth0Error.fromError(err)
      getLogger().error('failed to get logged in entity id', error)
      void this.auth0.logout()
    }
    return ''
  }
}

/**
 *
 * @param config
 */
export function auth0(config: Config): Auth0Plugin {
  return Auth.initialize(config).auth0
}

/**
 *
 */
export function getAccessToken(): Promise<string> {
  return Auth.getInstance().getAccessToken()
}

/**
 *
 */
export function useLoggedInEntityId(): string {
  return Auth.getInstance().getLoggedInEntityId()
}

/**
 * Safely get entityId without triggering logout when unauthenticated.
 * Use this on pages that support both authenticated and unauthenticated access.
 */
export function useOptionalEntityId(): {
  isAuthenticated: Ref<boolean>
  entityId: string
} {
  const { isAuthenticated, idTokenClaims } = useAuth0()
  const entityId = isAuthenticated.value
    ? ((idTokenClaims.value?.[ENTITY_ID_CLAIM] as string | undefined) ?? '')
    : ''
  return { isAuthenticated, entityId }
}
